<template>
  <HeaderBar />
  <section class="carvia-container space-y-6">
    <div>
      <CVButton v-if="$can('create', 'Video')" variant="secondary" @click="handleEditVideo(true)"
        >+ {{ t('addVideo') }}</CVButton
      >
    </div>

    <div v-if="isLoading" class="flex items-center justify-center">
      <Spinner class="text-hazel-600" />
    </div>

    <EntityListExceptions
      v-else
      :status="status"
      :error="error as any"
      :items-count="videos.length"
      :no-items-text="t('noVideosFound')"
    />

    <div v-if="videos.length" class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <button
        v-for="video in videos"
        :key="video.id"
        class="flex flex-col justify-start overflow-hidden rounded-lg bg-white text-left"
        @click="handleOpenVideo(video)"
      >
        <img v-if="video.thumbnailUrl" :src="video.thumbnailUrl" class="w-full" />
        <div
          v-else
          class="flex aspect-[3/2] w-full items-center justify-center bg-obsidian-700 text-base text-white"
        >
          {{ t('noThumbnail') }}
        </div>
        <div class="flex flex-col gap-2 p-3">
          <div class="line-clamp-2 text-base font-semibold">{{ video.title }}</div>
          <div class="line-clamp-3 text-sm text-obsidian-600">{{ video.description }}</div>
        </div>
      </button>
    </div>

    <div v-if="hasNextPage" class="border-primary/30 flex justify-center">
      <CVButton outline :is-loading="isFetchingNextPage" @click="fetchNextPage">
        {{ t('loadMore') }}
      </CVButton>
    </div>

    <Modal v-model="showModal" :content-class="'!max-w-5xl w-full'" :show-close="true">
      <VideoPlayer
        v-if="activeVideoId && !isEditMode"
        :video-id="activeVideoId"
        @edit="handleEditVideo()"
        @delete="handleDelete()"
      />
      <EditVideo
        v-else-if="isEditMode"
        :video-id="activeVideoId"
        @saved="handleCloseEdit"
        @cancel="handleCloseEdit"
      />
    </Modal>
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import Modal from '@/components/Modal.vue';
import { useI18n } from 'vue-i18n';
import { nextTick, ref, watch, watchEffect } from 'vue';
import { useDeleteVideo, useVideos } from '@/queries/use-videos';
import { Order, type Video } from '@carvia/ros-client-types';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import VideoPlayer from './VideoPlayer.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import EditVideo from './EditVideo.vue';
import { Alert } from '@/services/alert.service';
import { useUiStore } from '@/stores/ui.store';
import router from '@/router';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();

watchEffect(() => {
  uiStore.setHeaderTitle(t('trainingVideos'));
});

const { data, isLoading, status, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
  useVideos({ limit: 9, order: Order.DESC });
const videos = useFlattenPaginatedData(data);
const { mutateAsync: deleteVideo } = useDeleteVideo();

const showModal = ref(false);
const activeVideoId = ref<string | null>(null);
const isEditMode = ref(false);

function handleOpenVideo(video: Video) {
  isEditMode.value = false;
  activeVideoId.value = video.id;
  showModal.value = true;
  router.replace({ name: 'trainingVideos', query: { id: video.id } });
}

function handleEditVideo(createNew = false) {
  isEditMode.value = true;
  activeVideoId.value = createNew ? null : activeVideoId.value;
  showModal.value = true;
}

async function handleDelete() {
  if (!activeVideoId.value) return;
  const { isConfirmed } = await Alert.fire({
    titleText: t('confirmDeleteVideo'),
    icon: 'warning',
    buttons: [
      {
        text: t('delete'),
        style: 'danger',
        propertyName: 'isConfirmed',
      },
      {
        text: t('cancel'),
        style: 'ghost',
        propertyName: 'isDismissed',
      },
    ],
  });
  if (!isConfirmed) return;
  await deleteVideo(activeVideoId.value);
  activeVideoId.value = null;
  showModal.value = false;
}

function handleCloseEdit() {
  isEditMode.value = false;
  if (!activeVideoId.value) {
    showModal.value = false;
  }
}

watch(showModal, (newVal) => {
  if (!newVal) {
    router.replace({ name: 'trainingVideos' });
  }
});

const unwatchVideos = watch(
  videos,
  () => {
    if (videos.value && route.query.id) {
      const openedVideo = videos.value.find((video) => video.id === route.query.id) as Video;
      if (openedVideo) {
        handleOpenVideo(openedVideo);
        nextTick(() => {
          unwatchVideos();
        });
      }
    }
  },
  { immediate: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "trainingVideos": "Training videos",
    "noVideosFound": "No training videos found.",
    "videoUrlNotAvailable": "Video URL not available.",
    "addVideo": "Add training video",
    "confirmDeleteVideo": "Do you really want to delete this training video?",
    "noThumbnail": "No thumbnail"
  },
  "de": {
    "trainingVideos": "Schulungsvideos",
    "noVideosFound": "Keine Schulungsvideos gefunden.",
    "videoUrlNotAvailable": "Video-URL nicht verfügbar.",
    "addVideo": "Schulungsvideo hinzufügen",
    "confirmDeleteVideo": "Möchtest du dieses Schulungsvideo wirklich löschen?",
    "noThumbnail": "Kein Thumbnail"
  },
  "es": {
    "trainingVideos": "Vídeos de formación",
    "noVideosFound": "No se encontraron vídeos de formación.",
    "videoUrlNotAvailable": "URL del vídeo no disponible.",
    "addVideo": "Añadir vídeo de formación",
    "confirmDeleteVideo": "¿Seguro que desea eliminar este vídeo de formación?",
    "noThumbnail": "Sin miniatura"
  }
}
</i18n>
